import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Button, Space, Tag, Descriptions, Card, Table, Alert, Spin, Tooltip, Flex } from 'antd';
import { ArrowLeftOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../api/client';

const { Title, Text } = Typography;

// ServerSnapshot is the archived shape returned by GET /servers/:key/snapshot.
// The backend wraps the original server config under `server` and adds
// archival metadata at the top level (deletedAt, optionally deletedBy).
interface ProxyConfigResponse {
  protocol: string;
  host: string;
  port: number;
  login?: string;
  hasPassword: boolean;
  label?: string;
  country?: string;
}

interface ServerResponse {
  id: string;
  key: string;
  name: string;
  mainIP: string;
  networks: string[];
  proxies: ProxyConfigResponse[];
  checkMode: string;
  type?: 'dedicated' | 'mobile-gateway';
  country: string;
  city?: string;
  tags: string[];
  enabled: boolean;
  priority: number;
  sourceKey?: string;
  sourceId?: string;
  sourceUrl?: string;
  createdAt: string;
  updatedAt: string;
}

interface SnapshotPayload {
  key: string;
  deletedAt: string;
  deletedBy?: string;
  server: ServerResponse;
}

const ServerSnapshotPage: React.FC = () => {
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState<SnapshotPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSnapshot = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/servers/${key}/snapshot`);
      setSnapshot(data.data);
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Failed to load snapshot';
      // The page renders this in its own Alert; a toast on top said the same
      // thing again, twice over under StrictMode's double effect.
      setError(err.response?.status === 404
        ? 'No archived configuration found for this server — the link may be stale.'
        : msg);
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => {
    fetchSnapshot();
  }, [fetchSnapshot]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>;
  }

  if (error || !snapshot) {
    return (
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Back</Button>
        <Alert
          type="warning"
          showIcon
          message="Snapshot not available"
          description={error || 'This server has no archived configuration.'}
        />
      </Space>
    );
  }

  const s = snapshot.server;
  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      {/* Same header as the live server page: back, name, one state tag. */}
      <Space size={8} align="center">
        <Tooltip title="Back">
          <Button size="small" icon={<ArrowLeftOutlined />} aria-label="Back" onClick={() => navigate(-1)} />
        </Tooltip>
        <Title level={4} style={{ margin: 0 }} ellipsis={{ tooltip: s.name }}>{s.name}</Title>
        <Tag icon={<ExclamationCircleOutlined />} style={{ margin: 0 }}>ARCHIVED</Tag>
      </Space>

      <Alert
        type="info"
        showIcon
        message={
          <span>
            This server was deleted on <strong>{new Date(snapshot.deletedAt).toLocaleString()}</strong>
            {snapshot.deletedBy && <> by <strong>{snapshot.deletedBy}</strong></>}.
            The configuration below is a read-only snapshot preserved for historical reports.
          </span>
        }
      />

      <Card size="small" title="Configuration">
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="Key" span={2}>
            <Text code copyable>{s.key}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Main IP">{s.mainIP || '—'}</Descriptions.Item>
          <Descriptions.Item label="Country">{s.country || '—'}</Descriptions.Item>
          <Descriptions.Item label="Type">{s.type === 'mobile-gateway' ? 'Mobile gateway' : 'Dedicated'}</Descriptions.Item>
          <Descriptions.Item label="Check Mode">
            {s.checkMode === 'active-probing' ? 'Active Probing' : s.checkMode === 'agent-heartbeat' ? 'Agent Heartbeat' : s.checkMode}
          </Descriptions.Item>
          <Descriptions.Item label="Enabled">{s.enabled ? 'Yes' : 'No'}</Descriptions.Item>
          <Descriptions.Item label="Priority">{s.priority}</Descriptions.Item>
          <Descriptions.Item label="Networks" span={2}>
            {(s.networks || []).length === 0
              ? <Text type="secondary">No additional networks configured.</Text>
              : <Flex wrap gap={4}>{s.networks.map(n => <Tag key={n} style={{ margin: 0 }}>{n}</Tag>)}</Flex>}
          </Descriptions.Item>
          <Descriptions.Item label="Tags" span={2}>
            {(s.tags || []).length === 0 ? '—' : <Flex wrap gap={4}>{s.tags.map(t => <Tag key={t} style={{ margin: 0 }}>{t}</Tag>)}</Flex>}
          </Descriptions.Item>
          {s.sourceKey && (
            <Descriptions.Item label="Source" span={2}>
              <Text>{s.sourceKey}</Text>
              {s.sourceId && <Text type="secondary" style={{ marginLeft: 8 }}>ID {s.sourceId}</Text>}
              {s.sourceUrl && (
                <a href={s.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 8 }}>
                  {s.sourceUrl}
                </a>
              )}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Created">{new Date(s.createdAt).toLocaleString()}</Descriptions.Item>
          <Descriptions.Item label="Last Updated">{new Date(s.updatedAt).toLocaleString()}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" title={`Proxies (${(s.proxies || []).length})`}>
        {(s.proxies || []).length === 0 ? (
          <Text type="secondary">No proxies configured at the time of deletion.</Text>
        ) : (
          <Table
            size="small"
            pagination={(s.proxies || []).length > 20 ? { defaultPageSize: 20 } : false}
            rowKey={(r) => `${r.host}:${r.port}`}
            dataSource={s.proxies}
            columns={[
              { title: 'Protocol', dataIndex: 'protocol', width: 88 },
              { title: 'Host', dataIndex: 'host', ellipsis: { showTitle: true } },
              { title: 'Port', dataIndex: 'port', width: 72, align: 'right' as const },
              { title: 'Label', dataIndex: 'label', ellipsis: { showTitle: true }, render: (v: string) => v || '—' },
              { title: 'Country', dataIndex: 'country', width: 90, render: (v: string) => v || '—' },
              { title: 'Auth', dataIndex: 'hasPassword', width: 80, render: (v: boolean) => v ? 'Yes' : 'No' },
            ]}
          />
        )}
      </Card>
    </Space>
  );
};

export default ServerSnapshotPage;
